import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import WorkflowBranchOptions from './WorkflowBranchOptions';
import { getAllWorkflows, Workflow } from '../services/workflowService';
import TermsConfirmationModal from './modals/TermsConfirmationModal';
import { useAuth } from 'context/AuthContext';
import { ClientIntegrationList, clientIntegrationService } from 'services/clientIntegrationService';
import ConfirmationModal from './modals/ConfirmationModal';
import api, { API_BASE_URL } from 'services/api';

type FileSelectionSlot = { id: string; files: File[] };

const newSlotId = () => `slot-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const WORKFLOW_TYPE_MIN_IMAGES: Record<string, number> = {
  books: 5,
  stamps: 1,
  postcards: 2,
  maps: 4,
  printsandposters: 2,
  fdc: 1,
};

function workflowTypeToUploadKey(workflowType: string | null | undefined): string | null {
  if (!workflowType || !String(workflowType).trim()) return null;
  const normalized = String(workflowType)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z]/g, '');

  if (normalized === 'book' || normalized === 'books') return 'books';
  if (normalized === 'stamp' || normalized === 'stamps') return 'stamps';
  if (normalized === 'postcard' || normalized === 'postcards') return 'postcards';
  if (normalized === 'map' || normalized === 'maps' || normalized === 'mapsandprints') return 'maps';
  if (normalized === 'printsandposters') return 'printsandposters';
  if (normalized === 'fdc') return 'fdc';
  return null;
}

export const filePreviewKey = (slotId: string, file: File) =>
  `${slotId}::${file.name}::${file.size}::${file.lastModified}`;

const MIN_FILE_SIZE_KB = 150;
const DEBUG_EBAY_TASK_ID = 'task-40-12312834644';

const isFileSizeValid = (file: File) => file.size / 1024 >= MIN_FILE_SIZE_KB;

function reorderFilesInSlot(files: File[], fromIndex: number, toIndex: number): File[] {
  if (fromIndex === toIndex) return files;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= files.length || toIndex >= files.length) return files;
  const next = [...files];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

const REORDER_ARM_DIST_PX = 10;
const REORDER_ARM_DIST_SQ = REORDER_ARM_DIST_PX * REORDER_ARM_DIST_PX;
const REORDER_ARM_DELAY_MS = 220;
const NO_TOUCH_DRAG_STYLE: React.CSSProperties = {
  WebkitTouchCallout: 'none',
  touchAction: 'none',
};

function resolveReorderTargetIndex(slotId: string, clientX: number, clientY: number, fileCount: number): number {
  if (fileCount <= 0) return 0;
  const sel = `[data-reorder-cell="${CSS.escape(slotId)}"]`;
  const nodes = document.querySelectorAll(sel);
  if (nodes.length === 0) return 0;
  let hitIndex = -1;
  let best = 0;
  let bestDist = Infinity;
  nodes.forEach((node, i) => {
    const r = node.getBoundingClientRect();
    const inside = clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
    if (inside) hitIndex = i;
    const cx = (r.left + r.right) / 2;
    const cy = (r.top + r.bottom) / 2;
    const d = (clientX - cx) ** 2 + (clientY - cy) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  const idx = hitIndex >= 0 ? hitIndex : best;
  return Math.max(0, Math.min(fileCount - 1, idx));
}

type ImageReorderState = {
  slotId: string;
  fromIndex: number;
  clientX: number;
  clientY: number;
  targetIndex: number;
};

type ReorderPointerSession = {
  slotId: string;
  index: number;
  pointerId: number;
  startX: number;
  startY: number;
  startTime: number;
  captureEl: HTMLElement;
};

const ImageUpload: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [fileSlots, setFileSlots] = useState<FileSelectionSlot[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [imagePreviews, setImagePreviews] = useState<{ [key: string]: string }>({});
  const [draggingSlotId, setDraggingSlotId] = useState<string | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [integrations, setIntegrations] = useState<ClientIntegrationList[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<ClientIntegrationList | null>(null);
  const [loadingWorkflows, setLoadingWorkflows] = useState<boolean>(true);
  const [branchOptions, setBranchOptions] = useState<{ [key: string]: boolean }>({});
  const [termsOfService, setTermsOfService] = useState<boolean>(false);
  const [rememberSettings, setRememberSettings] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isDeleteInputModalOpen, setIsDeleteInputModalOpen] = useState('');
  const booksRelatedTypes = ['books', 'maps', 'printsandposters'];
  const { user } = useAuth();

  const [imageReorder, setImageReorder] = useState<ImageReorderState | null>(null);
  const fileSlotsRef = useRef(fileSlots);
  fileSlotsRef.current = fileSlots;
  const imageReorderRef = useRef<ImageReorderState | null>(null);
  const reorderPointerRef = useRef<ReorderPointerSession | null>(null);

  const commitImageReorder = useCallback((next: ImageReorderState | null) => {
    imageReorderRef.current = next;
    setImageReorder(next);
  }, []);

  const finishImageReorder = useCallback(() => {
    const sess = reorderPointerRef.current;
    const active = imageReorderRef.current;
    if (sess?.captureEl) {
      try {
        sess.captureEl.releasePointerCapture(sess.pointerId);
      } catch {
        //
      }
    }
    reorderPointerRef.current = null;
    if (active && active.fromIndex !== active.targetIndex) {
      setFileSlots(prev =>
        prev.map(slot =>
          slot.id !== active.slotId
            ? slot
            : { ...slot, files: reorderFilesInSlot(slot.files, active.fromIndex, active.targetIndex) }
        )
      );
    }
    imageReorderRef.current = null;
    setImageReorder(null);
  }, []);

  const handleReorderPointerDown = (slotId: string, fileIndex: number) => (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const target = e.target;
    if (target instanceof Element && target.closest('button')) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    reorderPointerRef.current = {
      slotId,
      index: fileIndex,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startTime: Date.now(),
      captureEl: el,
    };
  };

  const handleReorderPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const sess = reorderPointerRef.current;
    if (!sess || sess.pointerId !== e.pointerId) return;
    e.preventDefault();

    if (!imageReorderRef.current) {
      const dx = e.clientX - sess.startX;
      const dy = e.clientY - sess.startY;
      if (dx * dx + dy * dy < REORDER_ARM_DIST_SQ && Date.now() - sess.startTime < REORDER_ARM_DELAY_MS) return;
      const n = fileSlotsRef.current.find(s => s.id === sess.slotId)?.files.length ?? 0;
      const targetIndex = resolveReorderTargetIndex(sess.slotId, e.clientX, e.clientY, n);
      commitImageReorder({
        slotId: sess.slotId,
        fromIndex: sess.index,
        clientX: e.clientX,
        clientY: e.clientY,
        targetIndex,
      });
      return;
    }

    const active = imageReorderRef.current;
    const n = fileSlotsRef.current.find(s => s.id === active.slotId)?.files.length ?? 0;
    const targetIndex = resolveReorderTargetIndex(active.slotId, e.clientX, e.clientY, n);
    commitImageReorder({
      ...active,
      clientX: e.clientX,
      clientY: e.clientY,
      targetIndex,
    });
  };

  const handleReorderPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const sess = reorderPointerRef.current;
    if (!sess || sess.pointerId !== e.pointerId) return;
    finishImageReorder();
  };

  const handleReorderLostCapture = () => {
    if (reorderPointerRef.current || imageReorderRef.current) {
      finishImageReorder();
    }
  };

  const uploadTypeKey = React.useMemo(
    () => workflowTypeToUploadKey(selectedWorkflow?.workflow_type),
    [selectedWorkflow?.workflow_type]
  );

  const minImagesPerSlot = React.useMemo(() => {
    if (!uploadTypeKey) return 0;
    return WORKFLOW_TYPE_MIN_IMAGES[uploadTypeKey] ?? 1;
  }, [uploadTypeKey]);

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const allWorkflows = await getAllWorkflows();
        const nonTriageWorkflows = allWorkflows.filter(w => w.workflow_type !== 'triage');
        setWorkflows(nonTriageWorkflows);
        if (nonTriageWorkflows.length > 0) {
          setSelectedWorkflow(nonTriageWorkflows[0]);
        }
      } catch (error) {
        console.error('Failed to fetch workflows:', error);
        setUploadStatus('Failed to load workflows. Please refresh the page.');
      } finally {
        setLoadingWorkflows(false);
      }
    };

    fetchWorkflows();
    fetchIntegrations();
  }, []);

  useEffect(() => {
    if (uploadTypeKey) {
      setFileSlots([{ id: newSlotId(), files: [] }]);
      setImagePreviews({});
      setUploadStatus('');
      setDraggingSlotId(null);
      imageReorderRef.current = null;
      reorderPointerRef.current = null;
      setImageReorder(null);
    } else {
      setFileSlots([]);
      imageReorderRef.current = null;
      reorderPointerRef.current = null;
      setImageReorder(null);
    }
  }, [uploadTypeKey, selectedWorkflow?.id]);

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    void _event;
  };

  const fetchIntegrations = async () => {
    if (!user?.clientId) return;

    try {
      const integrationsList = (await clientIntegrationService.getClientIntegrationsList(user.clientId)).filter(item => item.is_active);
      setIntegrations(integrationsList);
      if (integrationsList.length) {
        setSelectedIntegration(integrationsList[0]);
      }
    } catch (err) {
      console.error('Error loading integrations:', err);
    }
  }

  const handleDeleteAccept = () => {
    removeFileSelectionSlot(isDeleteInputModalOpen);
    setIsDeleteInputModalOpen('');
  };

  const handleTermsAccept = () => {
    setIsTermsModalOpen(false);
    handleUpload();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, slotId: string) => {
    if (event.target.files && event.target.files.length > 0) {
      processFiles(Array.from(event.target.files), slotId);
    }
    event.target.value = '';
  };

  const handleDragOver = (slotId: string) => (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingSlotId(slotId);
  };

  const handleDragLeave = (slotId: string) => (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingSlotId(prev => (prev === slotId ? null : prev));
  };

  const handleDrop = (slotId: string) => (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingSlotId(null);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const imageFiles = droppedFiles.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      processFiles(imageFiles, slotId);
    }
  };

  const processFiles = (filesArray: File[], slotId: string) => {
    const prefix = `${slotId}::`;
    setFileSlots(prev => prev.map(slot => (slot.id === slotId ? { ...slot, files: filesArray } : slot)));
    setUploadStatus('');
    setImagePreviews(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(prefix)) delete next[k];
      });
      return next;
    });

    filesArray.forEach(file => {
      const reader = new FileReader();
      const key = filePreviewKey(slotId, file);
      reader.onloadend = () => {
        setImagePreviews(prev => ({
          ...prev,
          [key]: typeof reader.result === 'string' ? reader.result : ''
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (slotId: string, file: File) => {
    const key = filePreviewKey(slotId, file);
    setFileSlots(prev =>
      prev.map(slot =>
        slot.id === slotId ? { ...slot, files: slot.files.filter(f => f !== file) } : slot
      )
    );
    setImagePreviews(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const removeFileSelectionSlot = (slotId: string) => {
    const prefix = `${slotId}::`;
    setFileSlots(prev => prev.filter(s => s.id !== slotId));
    setImagePreviews(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(prefix)) delete next[k];
      });
      return next;
    });
    setUploadStatus('');
  };

  const addFileSelectionSlot = () => {
    setFileSlots(prev => [...prev, { id: newSlotId(), files: [] }]);
  };

  const totalFilesCount = fileSlots.reduce((n, s) => n + s.files.length, 0);

  const fileSizePipelineValid = React.useMemo(() => {
    if (totalFilesCount === 0) return false;
    return fileSlots.every(slot => slot.files.every(isFileSizeValid));
  }, [fileSlots, totalFilesCount]);

  const slotMinimumsMet = React.useMemo(() => {
    if (!uploadTypeKey || minImagesPerSlot <= 0 || fileSlots.length === 0) return false;
    return fileSlots.every(slot => slot.files.length >= minImagesPerSlot);
  }, [uploadTypeKey, minImagesPerSlot, fileSlots]);

  const pipelineValid = fileSizePipelineValid && slotMinimumsMet;

  const handleUpload = async () => {
    if (totalFilesCount === 0) {
      setUploadStatus(t('imageUpload.pleaseSelectFile'));
      return;
    }

    if (!fileSizePipelineValid) {
      setUploadStatus(t('imageUpload.fixErrors'));
      return;
    }

    if (!slotMinimumsMet) {
      setUploadStatus(
        `Each upload group needs at least ${minImagesPerSlot} image${minImagesPerSlot === 1 ? '' : 's'} for ${selectedWorkflow?.workflow_type?.trim() ? selectedWorkflow.workflow_type : 'this workflow type'}.`
      );
      return;
    }

    if (!selectedWorkflow) {
      setUploadStatus('Please select a workflow');
      return;
    }

    history.push('/image-processing', {
      fileGroups: fileSlots.map(s => [...s.files]),
      slotIds: fileSlots.map(s => s.id),
      files: fileSlots.flatMap(s => s.files),
      imagePreviews,
      validationPassed: true,
      selectedWorkflow: selectedWorkflow,
      platforms: integrations,
      branchOptions: branchOptions
    });
  };

  const handleDebugEbayTask = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/client-integrations/ebay-feed-task/${DEBUG_EBAY_TASK_ID}`);
      setUploadStatus(`eBay feed task response: ${JSON.stringify(response.data)}`);
    } catch (error) {
      console.error('Failed to fetch eBay feed task:', error);
      setUploadStatus('Failed to fetch eBay feed task');
    }
  };

  return (
    <div className="card">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('imageUpload.title')}</h2>
        {/* <div className="flex items-center gap-3">
          <p className="text-gray-600 dark:text-gray-400">{t('imageUpload.subtitle')}</p>
          <button
            type="button"
            onClick={handleDebugEbayTask}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            Debug eBay task
          </button>
        </div> */}
      </div>

      {/* Workflow Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Workflow
        </label>
        {loadingWorkflows ? (
          <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading workflows...</span>
          </div>
        ) : workflows.length === 0 ? (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              No workflows available. Please contact an administrator to create workflows.
            </p>
          </div>
        ) : (
          <>
            <select
              value={selectedWorkflow?.id || ''}
              onChange={(e) => {
                const workflow = workflows.find(w => w.id === parseInt(e.target.value));
                setSelectedWorkflow(workflow || null);
                // Reset branch options when workflow changes
                setBranchOptions({});
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name} - {workflow.workflow_type?.trim() || '—'}
                </option>
              ))}
            </select>

            {/* Display selected workflow details */}
            {selectedWorkflow && (
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  {selectedWorkflow.name}
                </h3>
                <p className="text-xs text-blue-700 dark:text-blue-200">
                  {selectedWorkflow.description || 'No description available'}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-blue-600 dark:text-blue-300">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-800">
                    Type:{' '}
                    {selectedWorkflow.workflow_type?.trim()
                      ? selectedWorkflow.workflow_type
                      : '— (not set on workflow)'}
                  </span>
                  {uploadTypeKey ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-800">
                      Min. images per upload group: {minImagesPerSlot}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100">
                      Set workflow type (Books, Stamps, …) on this workflow in Workflow Management to enable uploads.
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Platform
        </label>
        {integrations.length === 0 ? (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              No API available. Please configure API integrations.
            </p>
          </div> 
        ) : (
          <>
            <select
              value={selectedIntegration?.platform || ''}
              onChange={(e) => {
                const integration = integrations.find(w => w.id === parseInt(e.target.value));
                setSelectedIntegration(integration || null);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {integrations.map((integration) => (
                <option key={integration.id} value={integration.id}>
                  {integration.platform}
                </option>
              ))}
            </select>
          </>
        )}
      </div> */}

      {/* <div className="mt-6 mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          {t('imageUpload.itemType')}
        </h3>
        {selectedIntegration && (
          <div className="flex flex-col gap-1">
            {selectedIntegration.platform !== "hippostcard" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-4 h-4 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="stamps"
                  checked={selectedType === "stamps"}
                  onChange={handleChange}
                />
                  Stamps (1 image)
              </label>
            }

            {selectedIntegration.platform !== "hipstamp" && selectedIntegration.platform !== "hippostcard" && selectedIntegration.platform !== "delcampe" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-5 h-5 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="books"
                  checked={selectedType === "books"}
                  onChange={handleChange}
                />
                  Books (minimum 5 images: title page, binding, copyright page or half title page, first page with visible pagination, lat page with visible pagination)
              </label>
            }
            
            {selectedIntegration.platform !== "hipstamp" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-4 h-4 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="postcards"
                  checked={selectedType === "postcards"}
                  onChange={handleChange}
                />
                  Postcards (2 images front and verso)
              </label>
            }

            {selectedIntegration.platform !== "hipstamp" && selectedIntegration.platform !== "hippostcard" && selectedIntegration.platform !== "delcampe" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-4 h-4 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="maps"
                  checked={selectedType === "maps"}
                  onChange={handleChange}
                />
                  Maps (4 images: full item image, cartouche, one additional photo of a detail, verso)
              </label>
            }
            {selectedIntegration.platform !== "hipstamp" && selectedIntegration.platform !== "hippostcard" && selectedIntegration.platform !== "delcampe" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-4 h-4 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="printsandposters"
                  checked={selectedType === "printsandposters"}
                  onChange={handleChange}
                />
                  Prints and Posters (2 images: front and verso)
              </label>
            }
            {selectedIntegration.platform !== "hippostcard" &&
              <label className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 cursor-pointer">
                <input
                  className="w-4 h-4 cursor-pointer"
                  type="radio"
                  name="uploadType"
                  value="fdc"
                  checked={selectedType === "fdc"}
                  onChange={handleChange}
                />
                  FDC - First Day Cover (1 image)
              </label>
            }
          </div>
        )}
      </div> */}

      {/* File Upload Area — one block per selection; data is File[][] via fileSlots */}
      {uploadTypeKey && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('imageUpload.selectImageFile')}
          </label>
          <ul className="ml-1 mb-3 space-y-2 text-sm list-none p-0 m-0">
            <li className="flex items-start text-gray-500 dark:text-gray-400">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0 mt-1.5" aria-hidden />
              <span>Please upload only one type of item per session</span>
            </li>
            <li className="flex items-start text-gray-500 dark:text-gray-400">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0 mt-1.5" aria-hidden />
              <span>
                Please ensure that the pictures uploaded are over {MIN_FILE_SIZE_KB} KB in size
              </span>
            </li>
            <li className="flex items-start text-gray-600 dark:text-gray-400">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0 mt-1.5" aria-hidden />
              <span>{t('imageUpload.reorderHint')}</span>
            </li>
            <li className="flex items-start text-gray-600 dark:text-gray-400">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0 mt-1.5" aria-hidden />
              <span>
                Each file selection below must include at least {minImagesPerSlot} image
                {minImagesPerSlot === 1 ? '' : 's'} for this workflow type (
                {selectedWorkflow?.workflow_type?.trim() || '—'}
                ).
              </span>
            </li>
          </ul>

          <div className="space-y-6">
            {fileSlots.map((slot, slotIndex) => (
              <div
                key={slot.id}
                className={`rounded-lg border border-gray-200 dark:border-gray-600 p-4 bg-gray-50/80 dark:bg-gray-800/40 ${
                  imageReorder?.slotId === slot.id
                    ? 'relative z-[10000] ring-2 ring-blue-500/40 dark:ring-blue-400/50 isolate'
                    : ''
                }`}
                style={
                  imageReorder?.slotId === slot.id
                    ? {
                        boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.55)',
                      }
                    : undefined
                }
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Upload group {slotIndex + 1}
                  </p>
                  <p
                    className={`text-xs font-medium ${
                      slot.files.length >= minImagesPerSlot
                        ? 'text-green-700 dark:text-green-200'
                        : 'text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {slot.files.length} / {minImagesPerSlot} minimum image
                    {minImagesPerSlot === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="file"
                    onChange={e => handleFileChange(e, slot.id)}
                    accept="image/*"
                    multiple
                    className="hidden"
                    id={`file-upload-${slot.id}`}
                  />
                  <label
                    htmlFor={`file-upload-${slot.id}`}
                    onDragOver={handleDragOver(slot.id)}
                    onDragLeave={handleDragLeave(slot.id)}
                    onDrop={handleDrop(slot.id)}
                    className={`cursor-pointer flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg transition-colors ${
                      draggingSlotId === slot.id
                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                      </svg>
                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">{t('imageUpload.clickToUpload')}</span> {t('imageUpload.dragAndDrop')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('imageUpload.multipleFiles')}</p>
                    </div>
                  </label>
                </div>
                {fileSlots.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setIsDeleteInputModalOpen(slot.id)}
                    className="w-full py-3 px-4 rounded-lg font-medium transition duration-200 bg-red-600 hover:bg-red-700 text-white dark:bg-red-500 dark:hover:bg-red-600 mt-4"
                  >
                    {t('imageUpload.removeFileInput')}
                  </button>
                )}

                {slot.files.length > 0 && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {slot.files.map((file, index) => {
                      const pKey = filePreviewKey(slot.id, file);
                      const fileSizeOk = isFileSizeValid(file);
                      const reorderActive = imageReorder?.slotId === slot.id;
                      const isDragSource = reorderActive && imageReorder.fromIndex === index;
                      const isDropTarget = reorderActive && imageReorder.targetIndex === index;
                      return (
                        <div
                          key={pKey}
                          data-reorder-cell={slot.id}
                          className={`flex flex-col gap-1 select-none ${
                            reorderActive ? 'cursor-grabbing' : 'cursor-grab'
                          } ${isDragSource ? 'opacity-40' : ''} ${
                            isDropTarget ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-800 rounded-md' : ''
                          } touch-none`}
                          style={NO_TOUCH_DRAG_STYLE}
                          onPointerDown={handleReorderPointerDown(slot.id, index)}
                          onPointerMove={handleReorderPointerMove}
                          onPointerUp={handleReorderPointerUp}
                          onPointerCancel={handleReorderPointerUp}
                          onLostPointerCapture={handleReorderLostCapture}
                          onContextMenu={e => e.preventDefault()}
                        >
                          <div
                            className={`p-3 rounded-md border ${
                              fileSizeOk
                                ? 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-800'
                                : 'bg-red-50 dark:bg-red-950 border-red-400 dark:border-red-600'
                            }`}
                          >
                            <div className="flex flex-col">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center flex-1 min-w-0">
                                  <svg
                                    className={`w-5 h-5 mr-2 flex-shrink-0 ${
                                      fileSizeOk ? 'text-blue-500 dark:text-blue-300' : 'text-red-500 dark:text-red-400'
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                  </svg>
                                  <span
                                    className={`text-sm font-medium truncate ${
                                      fileSizeOk ? 'text-blue-800 dark:text-blue-200' : 'text-red-900 dark:text-red-100'
                                    }`}
                                  >
                                    {file.name}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFile(slot.id, file)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 ml-2 flex-shrink-0"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                              <div
                                className={`text-xs mb-2 ${
                                  fileSizeOk ? 'text-blue-600 dark:text-blue-300' : 'text-red-700 dark:text-red-300'
                                }`}
                              >
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                              {imagePreviews[pKey] && (
                                <div className="mt-2">
                                  <img
                                    src={imagePreviews[pKey]}
                                    alt={`Preview ${index + 1}`}
                                    draggable={false}
                                    onDragStart={e => e.preventDefault()}
                                    onContextMenu={e => e.preventDefault()}
                                    className={`w-full h-48 object-cover rounded border shadow-sm ${
                                      fileSizeOk
                                        ? 'border-blue-200 dark:border-blue-700'
                                        : 'border-red-400 dark:border-red-600'
                                    }`}
                                    style={NO_TOUCH_DRAG_STYLE}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {!fileSizeOk && (
                            <p className="text-sm font-medium text-red-700 dark:text-red-300 px-0.5">
                              {t('imageUpload.fileSizeTooSmall')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {imageReorder &&
            (() => {
              const reorderSlot = fileSlots.find(s => s.id === imageReorder.slotId);
              const draggedFile = reorderSlot?.files[imageReorder.fromIndex];
              const ghostSrc =
                draggedFile != null ? imagePreviews[filePreviewKey(imageReorder.slotId, draggedFile)] : '';
              if (!ghostSrc) return null;
              return (
                <div
                  className="fixed z-[10001] pointer-events-none w-44 rounded-lg border-2 border-blue-500 dark:border-blue-400 shadow-2xl overflow-hidden bg-gray-900"
                  style={{
                    left: imageReorder.clientX,
                    top: imageReorder.clientY,
                    transform: 'translate(-50%, -50%)',
                  }}
                  aria-hidden
                >
                  <img src={ghostSrc} alt="" className="w-full h-40 object-cover block" draggable={false} />
                </div>
              );
            })()}
        </div>
      )}

      {/* File size pipeline — single summary (per-image errors shown on thumbnails above) */}
      {uploadTypeKey && totalFilesCount > 0 && (
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-600 pb-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Pipeline</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Your images will go through the following validation steps
            </p>
          </div>

          <div className="space-y-3">
            <div
              className={`p-4 rounded-lg border ${
                fileSizePipelineValid
                  ? 'bg-green-50 dark:bg-transparent border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-950/60 border-red-200 dark:border-red-800'
              }`}
            >
              <div className="flex items-start gap-3">
                {fileSizePipelineValid ? (
                  <svg className="h-5 w-5 text-green-600 dark:text-green-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-600 dark:text-red-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Step 1: File size validation</h4>
                  <p
                    className={`text-sm mt-1 ${
                      fileSizePipelineValid ? 'text-green-800 dark:text-green-100' : 'text-red-800 dark:text-red-100'
                    }`}
                  >
                    {fileSizePipelineValid ? t('imageUpload.fileSizeValid') : t('imageUpload.fileSizeInvalidSummary')}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`p-4 rounded-lg border ${
                slotMinimumsMet
                  ? 'bg-green-50 dark:bg-transparent border-green-200 dark:border-green-800'
                  : 'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800'
              }`}
            >
              <div className="flex items-start gap-3">
                {slotMinimumsMet ? (
                  <svg className="h-5 w-5 text-green-600 dark:text-green-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-black dark:text-gray-100 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Step 2: Minimum images per upload group
                  </h4>
                  <p
                    className={`text-sm mt-1 ${
                      slotMinimumsMet ? 'text-green-800 dark:text-green-100' : 'text-black dark:text-gray-400'
                    }`}
                  >
                    {slotMinimumsMet
                      ? `Every group has at least ${minImagesPerSlot} image${minImagesPerSlot === 1 ? '' : 's'}.`
                      : `Each upload group needs at least ${minImagesPerSlot} image${
                          minImagesPerSlot === 1 ? '' : 's'
                        }. Add more images or remove empty groups.`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {uploadTypeKey && (
        <div className="mb-4">
          <button
            type="button"
            onClick={addFileSelectionSlot}
            className="w-full py-3 px-4 rounded-lg font-medium transition duration-200 bg-green-600 hover:bg-green-700 text-white dark:bg-green-500 dark:hover:bg-green-600 mb-4"
          >
            Add file selection
          </button>
          <button
            type="button"
            onClick={() => setIsTermsModalOpen(true)}
            disabled={
              totalFilesCount === 0 ||
              !fileSizePipelineValid ||
              !slotMinimumsMet ||
              !termsOfService ||
              workflows.length === 0
            }
            className={`w-full py-3 px-4 rounded-lg font-medium transition duration-200 ${
              totalFilesCount > 0 && fileSizePipelineValid && slotMinimumsMet && termsOfService
                ? 'bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {totalFilesCount === 0
              ? t('imageUpload.selectFileFirst')
              : workflows.length === 0
              ? t('imageUpload.noApiIntegrations')
              : !fileSizePipelineValid
              ? t('imageUpload.fixValidationErrors')
              : !slotMinimumsMet
              ? `Need at least ${minImagesPerSlot} image${minImagesPerSlot === 1 ? '' : 's'} per group`
              : !termsOfService
              ? t('imageUpload.acceptTermsAndConditions')
              : `${t('imageUpload.uploadAndAnalyze')} (${totalFilesCount} ${totalFilesCount === 1 ? 'image' : 'images'})`}
          </button>
        </div>
      )}

      {/* Status Message */}
      {uploadStatus && (
        <div className={`p-4 rounded-md ${
          uploadStatus.includes('successful') 
            ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-200'
            : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {uploadStatus.includes('successful') ? (
                <svg className="h-5 w-5 text-green-400 dark:text-green-300" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{uploadStatus}</p>
            </div>
          </div>
        </div>
      )}
      {uploadTypeKey && (
        <div className="flex flex-col items-center pt-2 gap-2">
          <label className="select-none ms-2 text-sm font-medium text-heading flex content-center gap-1 dark:text-gray-400">
            <input
              className="w-4 h-4 text-purple-600 bg-white dark:bg-purple-800 rounded cursor-pointer pt-1"
              type="checkbox"
              checked={termsOfService}
              onChange={(e) => setTermsOfService(e.target.checked)}
            />
            {t('imageUpload.agreeToToS')}
          </label>
          {/* <label className="select-none ms-2 text-sm font-medium text-heading flex content-center gap-1 dark:text-gray-400">
            <input 
              className="w-4 h-4 text-purple-600 bg-white dark:bg-purple-800 rounded cursor-pointer pt-1"
              type="checkbox"
              checked={rememberSettings}
              onChange={(e) => setRememberSettings(e.target.checked)}
            />
            {t('imageUpload.rememberChoice')}
          </label> */}
        </div>
      )}
      
      {/* Supported Items Info */}
      {/* <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('imageUpload.supportedItems')}</h3>
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            {t('imageUpload.firstDayOfIssue')}
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            {t('imageUpload.postageStamp')}
          </div> */}

        {/* Workflow Branch Options */}
        {/* {selectedWorkflow && selectedWorkflow.workflow_json?.nodes && (
          <WorkflowBranchOptions
            workflowNodes={selectedWorkflow.workflow_json.nodes}
            selectedOptions={branchOptions}
            onOptionChange={(nodeId, checked) => {
              setBranchOptions(prev => ({
                ...prev,
                [nodeId]: checked
              }));
            }}
          />
        )}
        </div>
      </div> */}

      <TermsConfirmationModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onAccept={handleTermsAccept}
        text={
          uploadTypeKey && booksRelatedTypes.includes(uploadTypeKey)
            ? t('confirmations.booksMapsPrintsConfirmationText')
            : t('confirmations.stampsFdcPostcardConfirmationText')
        }
      />
      <ConfirmationModal
        isOpen={isDeleteInputModalOpen}
        onAccept={handleDeleteAccept}
        onClose={() => setIsDeleteInputModalOpen('')}
      />
    </div>
  );
};

export default ImageUpload;